import { useEffect, useMemo, useState } from "react";
import styles from "./DevicesView.module.scss";
import { Pagination, View, ViewHeader } from "@/components";
import { filterDevicesList } from "../../_constants/devices_list.const";
import { DevicesList } from "../devices-list";
import { DevicesToolbar } from "../devices-toolbar";
import { CreateDeviceDrawer } from "../create-device-drawer";
import { DeviceDetailDrawer } from "../device-detail-drawer";
import { useDevicesQuery } from "../../_hooks/useDevicesQuery";
import { useDeleteDeviceMutation } from "../../_hooks/useDeleteDeviceMutation";
import type { ApiDevice } from "../../_types/devices.types";
import { DeleteDeviceModal } from "../delete-device-modal";
import type {
  DeviceGroupFilter,
  DeviceStatusFilter,
  DeviceTypeTab,
} from "../../_constants/devices.const";

export const DevicesView = () => {
  const [typeTab, setTypeTab] = useState<DeviceTypeTab>("all");
  const [status, setStatus] = useState<DeviceStatusFilter>("all");
  const [group, setGroup] = useState<DeviceGroupFilter>("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ApiDevice | null>(null);

  const deleteDeviceMutation = useDeleteDeviceMutation();

  useEffect(() => {
    setPage(1);
  }, [typeTab, status, group]);

  const { data, isLoading, isError, isFetching, refetch } = useDevicesQuery({
    page,
    limit,
    deviceType: typeTab,
    deviceStatus: status,
  });

  const devices = useMemo(
    () => filterDevicesList(data?.items ?? [], group),
    [data?.items, group]
  );

  const meta = data?.meta ?? {
    total: 0,
    page,
    limit,
    totalPages: 1,
  };

  const openDetail = (id: string) => {
    setSelectedId(id);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
  };

  const openEdit = (device: ApiDevice) => {
    openDetail(device.id);
  };

  const actionsPending = deleteDeviceMutation.isPending;

  const confirmDeleteDevice = () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    void deleteDeviceMutation
      .mutateAsync(id)
      .then(() => {
        setPendingDelete(null);
        if (selectedId === id) closeDetail();
      })
      .catch(() => {});
  };

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="Devices"
          description="Track connected devices, review their status, and organize hardware-related data.<br />Use this section to keep device information under control."
        />

        <DevicesToolbar
          typeTab={typeTab}
          status={status}
          group={group}
          onTypeTabChange={setTypeTab}
          onStatusChange={setStatus}
          onGroupChange={setGroup}
          onCreateDeviceClick={() => setCreateOpen(true)}
        />

        <DevicesList
          devices={devices}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          onDeviceClick={openDetail}
          onEditDevice={openEdit}
          onRequestDeleteDevice={(d) => setPendingDelete(d)}
          actionsDisabled={actionsPending}
        />

        {meta.total > 0 ? (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.totalPages}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setLimit(next);
              setPage(1);
            }}
          />
        ) : null}

        <CreateDeviceDrawer isOpen={createOpen} onClose={() => setCreateOpen(false)} />

        <DeleteDeviceModal
          isOpen={pendingDelete !== null}
          deviceName={pendingDelete?.name ?? ""}
          onClose={() => setPendingDelete(null)}
          onConfirm={confirmDeleteDevice}
          isDeleting={deleteDeviceMutation.isPending}
        />

        <DeviceDetailDrawer deviceId={selectedId} isOpen={detailOpen} onClose={closeDetail} />
      </div>
    </View>
  );
};
